<template>
    <div>
        <form v-if="message.type != 'success'" @submit.prevent="submitForm" id="eventsForm">
            <div class="grid gap-6 mb-6 md:grid-cols-2">
                <div class="col-span-2">
                    <label for="name" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Event name") }}
                    </label>
                    <input type="text" id="name" v-model="form.name" name="name"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        :placeholder="$t('Event name')" required />
                    <!-- Error message -->
                    <p v-if="hasError('name')" class="text-red-500 text-sm mt-1">
                        {{ getError('name') }}
                    </p>
                </div>

                <div>
                    <label for="start" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Start") }}
                    </label>
                    <input type="datetime-local" id="start" v-model="form.start" name="start"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        required />
                    <!-- Error message -->
                    <p v-if="hasError('start')" class="text-red-500 text-sm mt-1">
                        {{ getError('start') }}
                    </p>
                </div>

                <div>
                    <label for="end" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("End") }}
                    </label>
                    <input type="datetime-local" id="end" v-model="form.end" name="end"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        required />
                    <!-- Error message -->
                    <p v-if="hasError('end')" class="text-red-500 text-sm mt-1">
                        {{ getError('end') }}
                    </p>
                </div>

                <div>
                    <label for="price" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Price") }}
                    </label>
                    <input type="number" step="0.01" id="price" v-model="form.price" name="price"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        required />
                    <!-- Error message -->
                    <p v-if="hasError('price')" class="text-red-500 text-sm mt-1">
                        {{ getError('price') }}
                    </p>
                </div>

                <div>
                    <label for="external_url" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Website URL") }}
                    </label>
                    <input type="url" id="external_url" v-model="form.external_url" name="external_url"
                        placeholder="https://..."
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        required />
                    <!-- Error message -->
                    <p v-if="hasError('external_url')" class="text-red-500 text-sm mt-1">
                        {{ getError('external_url') }}
                    </p>
                </div>

                <div>
                    <label for="location" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Location") }}
                    </label>
                    <input type="text" id="location" v-model="form.location" name="location"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        :placeholder="$t('Event location')" required />
                    <!-- Error message -->
                    <p v-if="hasError('location')" class="text-red-500 text-sm mt-1">
                        {{ getError('location') }}
                    </p>
                </div>

                <div>
                    <label for="categories" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Categories") }} ({{ $t('separate by comma') }})
                    </label>
                    <input type="text" id="categories" v-model="form.categories" name="categories"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white"
                        required />
                    <!-- Error message -->
                    <p v-if="hasError('categories')" class="text-red-500 text-sm mt-1">
                        {{ getError('categories') }}
                    </p>
                </div>

                <div class="col-span-2">
                    <label for="image_upload" class="block text-sm font-medium text-gray-900 dark:text-white">
                        {{ $t("Event Image") }}
                    </label>
                    <div class="text-xs dark:text-neutral-200 mb-2">
                        {{ $t('Upload an image in PNG, JPEG, JPG, or WEBP format (max. 2MB)') }}
                    </div>
                    <input type="file" id="image_upload" @change="handleImageUpload"
                        accept="image/png, image/jpeg, image/jpg, image/webp" name="image_upload"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-600 dark:placeholder-gray-400 dark:text-white" />
                    <!-- Error message -->
                    <p v-if="hasError('image_upload')" class="text-red-500 text-sm mt-1">
                        {{ getError('image_upload') }}
                    </p>
                </div>
            </div>


            <div class="mb-6">
                <label for="description" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    {{ $t("Event text") }}
                </label>
                <small_text_editor textareaName="description"></small_text_editor>
                <!-- Error message -->
                <p v-if="hasError('description')" class="text-red-500 text-sm mt-1">
                    {{ getError('description') }}
                </p>
            </div>

            <div class="flex items-center mb-6">
                <div class="flex items-center">
                    <div class="flex items-center">
                        <input id="tos" type="checkbox" v-model="form.tos" name="tos" required
                            class="w-[25px] h-[25px] text-primary bg-gray-100 border-gray-300 rounded-lg checked:bg-primary focus:ring-primary dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600">
                    </div>
                </div>
                <label for="tos" class="ms-2 text-sm font-medium text-gray-900 dark:text-neutral-300">
                    {{ $t("I agree with the") }}
                    <a :href="$termsUrl" class="text-primary hover:underline dark:text-primary">
                        {{ $t("terms and conditions") }}.
                    </a>
                </label>
                <!-- Error message -->
                <p v-if="hasError('tos')" class="text-red-500 text-sm mt-1">
                    {{ getError('tos') }}
                </p>
            </div>

            <button type="submit" :disabled="submitting"
                class="text-white bg-primary focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center">
                {{ submitting ? $t("Submitting...") : $t("Submit") }}
            </button>
        </form>

        <div v-else class="">
            <div class="py-8">
                <div class="text-center">
                    <div
                        class="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full dark:bg-green-700">
                        <svg class="h-12 w-12 text-green-600 dark:text-green-100" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"
                            data-slot="icon">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path>
                        </svg>
                    </div>
                    <h1 class="text-4xl font-extrabold text-primary">{{ message.text }}!</h1>
                    <p class="mt-4 text-lg text-gray-800 dark:text-neutral-300">
                        {{ $t('Your event has been successfully received and is now under review by our team') }}.
                    </p>

                    <p class="mt-4 text-sm text-gray-700 dark:text-neutral-400">
                        {{ $t('Once it has been checked and approved, it will appear on :app_url for everyone to see', {
                            app_url: $hostName
                        }) }}.<br />
                        {{ $t('We will notify you if any additional information is needed') }}.
                    </p>
                </div>
                <div class="mt-8 text-center">
                    <a :href="$baseUrl + '/events'"
                        class="inline-block px-6 py-2 text-lg font-medium text-white transition-transform rounded-full bg-primary hover:scale-105 active:scale-90">
                        {{ $t("Back to Events") }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
export default {
    data() {
        return {
            form: {
                name: "",
                start: "",
                end: "",
                price: "",
                external_url: "",
                location: "",
                image_upload: null,
                categories: "",
                description: "",
                tos: false,
            },
            message: {
                text: "",
                type: "", // 'success' or 'error'
            },
            validationErrors: {},
            submitting: false,
            termsUrl: "/platform/terms",
        };
    },

    methods: {
        async submitForm() {
            if (this.submitting) return;
            this.submitting = true;

            try {
                const formData = new FormData();

                // ---- Append Vue form data safely ----
                for (const key in this.form) {
                    const value = this.form[key];

                    if (key === "tos") {
                        formData.append(key, value ? 1 : 0);
                    }
                    else if (key === "image_upload" || key === "logo_upload") {
                        // append ONLY if file exists
                        if (value instanceof File) {
                            formData.append(key, value);
                        }
                        // nothing appended when empty → avoids validation error
                    }
                    else {
                        formData.append(key, value);
                    }
                }

                // ---- Sync real DOM inputs (in case of external components) ----
                const formElement = document.querySelector("#eventsForm");
                const inputs = formElement.querySelectorAll("input, textarea, select");

                inputs.forEach(input => {
                    const name = input.name;
                    if (!name) return;

                    if (input.type === "checkbox") {
                        formData.set(name, input.checked ? 1 : 0);
                    }
                    else if (input.type === "file") {
                        if (input.files.length > 0) {
                            formData.set(name, input.files[0]);
                        }
                        // If empty: do nothing
                    }
                    else {
                        formData.set(name, input.value);
                    }
                });

                // ---- CSRF ----
                await axios.get("/sanctum/csrf-cookie", { withCredentials: true });

                // ---- Submit ----
                const response = await axios.post(
                    "/api/submitevent",
                    formData,
                    {
                        withCredentials: true,
                        headers: {
                            Accept: "application/json",
                            "Accept-Language": document.documentElement.lang,
                        },
                    }
                );

                this.message = {
                    type: "success",
                    text: response.data.message ?? "Event submitted successfully!",
                };

                window.scrollTo({ top: 0, behavior: "smooth" });

                // ---- Reset form ----
                this.form = {
                    name: "",
                    start: "",
                    end: "",
                    price: "",
                    external_url: "",
                    location: "",
                    image_upload: null,
                    logo_upload: null,
                    categories: "",
                    description: "",
                    tos: false,
                };

                this.validationErrors = {};

            } catch (error) {
                this.submitting = false;

                this.message.type = "error";
                this.validationErrors = {};

                // Laravel validation error
                if (error.response?.data?.errors) {
                    this.validationErrors = error.response.data.errors;

                    this.message.text = error.response.data.message ?? "Validation error.";
                } else {
                    this.message.text = "Something went wrong.";
                }
            } finally {
                this.submitting = false;
            }
        },


        handleImageUpload(event) {
            const file = event.target.files[0];
            if (file) this.form.image_upload = file;
        },
        hasError(field) {
            return this.validationErrors[field] !== undefined;
        },
        getError(field) {
            return this.validationErrors[field] ? this.validationErrors[field][0] : "";
        }
    },
};
</script>
